import CryptoJS from "crypto-js";

class TokenManager {
  constructor() {
    this.TOKEN_KEY = "auth_token";
    this.REFRESH_TOKEN_KEY = "refresh_token";
    this.FINGERPRINT_KEY = "device_fingerprint";
    this.CSRF_TOKEN_KEY = "csrf_token";
    this.USER_KEY = "user";
    this.ACCOUNTS_KEY = "accounts";
    this.CURRENT_USER_ID_KEY = "current_user_id";
    this.TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000;
    this.SESSION_TIMEOUT = 30 * 24 * 60 * 60 * 1000;
    this.lastActivity = Date.now();
    this.refreshPromise = null;
    this.startTokenRefreshTimer();
    this.initializeTokens();
    // Initialize activity monitoring
    this.initActivityMonitoring();
    // Initialize fingerprint
    this.initFingerprint();
  }

  initializeTokens() {
    try {
      // Try to recover access token from user_tokens storage
      const currentUserId = this.secureRetrieve(this.CURRENT_USER_ID_KEY);
      if (currentUserId) {
        const userTokens = this.secureRetrieve("user_tokens") || {};
        const currentUserTokens = userTokens[currentUserId];
        console.log("use", currentUserTokens);
        if (currentUserTokens && currentUserTokens.accessToken) {
          this.accessTokenInMemory = currentUserTokens.accessToken;
          console.log("Token recovered from secure storage");
        }
      }

      // Fallback: try to get from legacy localStorage
      if (!this.accessTokenInMemory) {
        const legacyToken = localStorage.getItem(this.TOKEN_KEY);
        if (legacyToken && this.isTokenValid(legacyToken)) {
          this.accessTokenInMemory = legacyToken;
          console.log("Token recovered from legacy storage");

          // Migrate to secure storage
          this.migrateToSecureStorage(legacyToken);
        }
      }
    } catch (error) {
      console.error("Error initializing tokens:", error);
    }
  }

  // NEW METHOD: Migrate legacy tokens to secure storage
  migrateToSecureStorage(accessToken) {
    try {
      const decoded = this.decodeToken(accessToken);
      if (decoded?.id) {
        const refreshToken = localStorage.getItem(this.REFRESH_TOKEN_KEY);
        const user = JSON.parse(localStorage.getItem(this.USER_KEY));

        if (user) {
          this.updateUserTokens(decoded.id, accessToken, refreshToken);
          this.setUser(user);
        }
      }
    } catch (error) {
      console.error("Error migrating to secure storage:", error);
    }
  }
  // Generate device fingerprint
  async initFingerprint() {
    let fingerprint = this.getFingerprint();

    if (!fingerprint) {
      // Generate fingerprint based on browser characteristics
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      ctx.textBaseline = "top";
      ctx.font = "14px 'Arial'";
      ctx.fillStyle = "#f60";
      ctx.fillRect(125, 1, 62, 20);
      ctx.fillStyle = "#069";
      ctx.fillText("Browser Fingerprint", 2, 15);

      const canvasData = canvas.toDataURL();
      const navigatorData = [
        navigator.userAgent,
        navigator.language,
        navigator.platform,
        navigator.hardwareConcurrency,
        window.screen.width,
        window.screen.height,
        window.screen.colorDepth,
        new Date().getTimezoneOffset(),
      ].join("|");

      fingerprint = CryptoJS.SHA256(canvasData + navigatorData).toString();
      this.setFingerprint(fingerprint);
    }

    return fingerprint;
  }
  startTokenRefreshTimer() {
    // Check every 2 minutes if token needs refresh
    setInterval(
      async () => {
        if (this.shouldRefreshToken()) {
          try {
            console.log("Proactively refreshing token...");
            await this.refreshAccessToken();
          } catch (error) {
            console.error("Proactive token refresh failed:", error);
          }
        }
      },
      2 * 60 * 1000,
    ); // Check every 2 minutes
  }

  // NEW METHOD: Check if token should be refreshed
  shouldRefreshToken() {
    const token = this.getAccessToken();
    if (!token) return false;

    try {
      const decoded = this.decodeToken(token);
      const now = Date.now() / 1000;
      const timeUntilExpiry = (decoded.exp - now) * 1000;

      // Refresh if less than 5 minutes until expiry
      return (
        timeUntilExpiry < this.TOKEN_REFRESH_THRESHOLD && timeUntilExpiry > 0
      );
    } catch (error) {
      return false;
    }
  }

  initActivityMonitoring() {
    const events = ["mousedown", "keydown", "scroll", "touchstart"];

    events.forEach((event) => {
      document.addEventListener(event, () => {
        this.lastActivity = Date.now();
      });
    });

    setInterval(
      () => {
        if (Date.now() - this.lastActivity > this.SESSION_TIMEOUT) {
          this.handleSessionTimeout();
        }
      },
      5 * 60 * 1000,
    );
  }

  handleSessionTimeout() {
    const token = this.getAccessToken();

    // Only timeout if no valid token AND user has been inactive
    if (!token || !this.isTokenValid(token)) {
      const isAuthenticated = this.isAuthenticated();

      if (isAuthenticated) {
        this.clearCurrentSession();
        window.dispatchEvent(new CustomEvent("sessionTimeout"));
        window.location.href = "/login?reason=timeout";
      }
    }
  }

  // Secure storage with encryption
  secureStore(key, value) {
    try {
      const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(value),
        process.env.REACT_APP_STORAGE_KEY || "default-key",
      ).toString();

      localStorage.setItem(key, encrypted);
      return true;
    } catch (error) {
      console.error("Secure storage error:", error);
      return false;
    }
  }

  // Secure retrieval with decryption
  secureRetrieve(key) {
    try {
      const encrypted = localStorage.getItem(key);
      if (!encrypted) return null;

      const decrypted = CryptoJS.AES.decrypt(
        encrypted,
        process.env.REACT_APP_STORAGE_KEY || "default-key",
      ).toString(CryptoJS.enc.Utf8);

      return JSON.parse(decrypted);
    } catch (error) {
      console.error("Secure retrieval error:", error);
      return null;
    }
  }

  // Token storage methods
  setTokens(accessToken, refreshToken = null, csrfToken = null) {
    try {
      // Store access token in memory
      this.accessTokenInMemory = accessToken;

      // Also store in localStorage for immediate recovery (temporary)
      localStorage.setItem(this.TOKEN_KEY, accessToken);

      // Store refresh token securely
      if (refreshToken) {
        this.secureStore(this.REFRESH_TOKEN_KEY, refreshToken);
        localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken); // Temporary
      }

      // Store CSRF token
      if (csrfToken) {
        this.secureStore(this.CSRF_TOKEN_KEY, csrfToken);
      }

      // Decode and store user info
      const decoded = this.decodeToken(accessToken);
      if (decoded?.id) {
        this.updateUserTokens(decoded.id, accessToken, refreshToken);
      }

      return true;
    } catch (error) {
      console.error("Error setting tokens:", error);
      return false;
    }
  }

  // Get access token (from memory)
  getAccessToken() {
    if (this.accessTokenInMemory) {
      return this.accessTokenInMemory;
    }

    // Try to recover tokens
    this.initializeTokens();

    return this.accessTokenInMemory || null;
  }

  // Get refresh token
  getRefreshToken() {
    return this.secureRetrieve(this.REFRESH_TOKEN_KEY);
  }

  // Get CSRF token
  getCSRFToken() {
    return this.secureRetrieve(this.CSRF_TOKEN_KEY);
  }

  // Get fingerprint
  getFingerprint() {
    return this.secureRetrieve(this.FINGERPRINT_KEY);
  }

  // Set fingerprint
  setFingerprint(fingerprint) {
    return this.secureStore(this.FINGERPRINT_KEY, fingerprint);
  }

  // Update user tokens storage
  updateUserTokens(userId, accessToken, refreshToken) {
    const userTokens = this.secureRetrieve("user_tokens") || {};

    userTokens[userId] = {
      accessToken,
      refreshToken,
      timestamp: Date.now(),
    };

    this.secureStore("user_tokens", userTokens);
    this.secureStore(this.CURRENT_USER_ID_KEY, userId);
  }

  // Token validation
  isTokenValid(token) {
    if (!token) return false;

    try {
      const decoded = this.decodeToken(token);
      const now = Date.now() / 1000;

      // Check expiration with 30 second buffer
      return decoded.exp > now + 30;
    } catch (error) {
      return false;
    }
  }

  // Decode JWT token
  decodeToken(token) {
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );

      return JSON.parse(jsonPayload);
    } catch (error) {
      console.error("Error decoding token:", error);
      return null;
    }
  }

  // Check authentication status
  isAuthenticated() {
    // First check memory
    if (this.accessTokenInMemory) {
      return this.isTokenValid(this.accessTokenInMemory);
    }

    // Try to recover from secure storage
    this.initializeTokens();

    // Check again after recovery attempt
    if (this.accessTokenInMemory) {
      return this.isTokenValid(this.accessTokenInMemory);
    }

    return false;
  }

  // Refresh access token with deduplication
  async refreshAccessToken() {
    // Prevent multiple simultaneous refresh requests
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this._performRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  // Actual refresh implementation
  async _performRefresh() {
    const refreshToken = this.getRefreshToken();
    const fingerprint = await this.getFingerprint();

    if (!refreshToken) {
      throw new Error("No refresh token available");
    }

    try {
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:5000"
        }/api/auth/refresh`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Fingerprint": fingerprint,
          },
          credentials: "include",
          body: JSON.stringify({
            refreshToken,
            fingerprint,
          }),
        },
      );

      const data = await response.json();

      if (data.success) {
        this.setTokens(data.accessToken, data.refreshToken, data.csrfToken);

        if (data.user) {
          this.setUser(data.user);
        }

        return data.accessToken;
      } else {
        throw new Error(data.message || "Failed to refresh token");
      }
    } catch (error) {
      console.error("Token refresh error:", error);
      this.clearCurrentSession();
      throw error;
    }
  }

  // User management
  setUser(user) {
    try {
      this.secureStore(this.USER_KEY, user);
      this.secureStore(this.CURRENT_USER_ID_KEY, user.id);

      // Update accounts list
      const accounts = this.getAccounts();
      const existingIndex = accounts.findIndex((acc) => acc.id === user.id);

      if (existingIndex >= 0) {
        accounts[existingIndex] = user;
      } else {
        accounts.push(user);
      }

      this.secureStore(this.ACCOUNTS_KEY, accounts);

      // Dispatch event
      window.dispatchEvent(new Event("userChanged"));

      return true;
    } catch (error) {
      console.error("Error setting user:", error);
      return false;
    }
  }

  // Get current user
  getUser() {
    const user = this.secureRetrieve(this.USER_KEY);

    // Backwards compatibility - try regular localStorage
    if (!user) {
      try {
        const storedUser = localStorage.getItem(this.USER_KEY);
        if (storedUser) {
          return JSON.parse(storedUser);
        }
      } catch (error) {
        console.error("Error parsing user from localStorage:", error);
      }
    }

    return user;
  }

  // Get all accounts
  getAccounts() {
    const accounts = this.secureRetrieve(this.ACCOUNTS_KEY);

    // Backwards compatibility
    if (!accounts) {
      try {
        const storedAccounts = localStorage.getItem(this.ACCOUNTS_KEY);
        if (storedAccounts) {
          return JSON.parse(storedAccounts);
        }
        console.log("stored", storedAccounts);
      } catch (error) {
        console.error("Error parsing accounts from localStorage:", error);
      }
    }

    return accounts || [];
  }

  // Switch account
  async switchAccount(userId) {
    try {
      const accounts = this.getAccounts();
      const targetUser = accounts.find((acc) => acc.id === userId);

      if (!targetUser) {
        console.error("User not found in accounts");
        return false;
      }

      const userTokens = this.secureRetrieve("user_tokens") || {};
      const targetTokens = userTokens[userId];

      if (!targetTokens || !this.isTokenValid(targetTokens.accessToken)) {
        console.error("Invalid or expired token for user");
        return false;
      }

      // Switch tokens
      this.setTokens(targetTokens.accessToken, targetTokens.refreshToken);
      this.setUser(targetUser);

      window.dispatchEvent(new Event("userChanged"));

      return true;
    } catch (error) {
      console.error("Error switching account:", error);
      return false;
    }
  }

  // Clear current session
  clearCurrentSession() {
    this.accessTokenInMemory = null;

    // Clear all storage locations
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.CSRF_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.CURRENT_USER_ID_KEY);

    // Clear secure storage
    this.secureStore(this.TOKEN_KEY, null);
    this.secureStore(this.REFRESH_TOKEN_KEY, null);
    this.secureStore(this.CSRF_TOKEN_KEY, null);
    this.secureStore(this.USER_KEY, null);
    this.secureStore(this.CURRENT_USER_ID_KEY, null);
  }

  // Clear all data
  clearAll() {
    this.clearCurrentSession();
    localStorage.removeItem(this.ACCOUNTS_KEY);
    localStorage.removeItem(this.FINGERPRINT_KEY);
    localStorage.removeItem("user_tokens");
  }

  // Remove specific account
  removeAccount(userId) {
    try {
      const accounts = this.getAccounts();
      const filteredAccounts = accounts.filter((acc) => acc.id !== userId);
      this.secureStore(this.ACCOUNTS_KEY, filteredAccounts);

      const userTokens = this.secureRetrieve("user_tokens") || {};
      delete userTokens[userId];
      this.secureStore("user_tokens", userTokens);

      const currentUserId = this.secureRetrieve(this.CURRENT_USER_ID_KEY);
      if (currentUserId === userId) {
        this.clearCurrentSession();
      }

      return true;
    } catch (error) {
      console.error("Error removing account:", error);
      return false;
    }
  }

  // Backwards compatibility methods
  removeTokens() {
    this.clearCurrentSession();
  }
}

export default new TokenManager();
